import { buttonClassName, ButtonVariant } from '@/components/form/buttonHelper';
import { FileInput } from '@/components/form/fields/file/FileInput';
import { Step } from '@/components/multistep/Questionnaire';
import { useControls } from '@/components/multistep/useMultistep';
import SiDownload from '@/icons/SiDownload';
import { useCSV } from '@/services/csv/useCSVService';
import { useCallback, useMemo } from 'react';
import { IncomingRateCard } from '@/services/csv/helpers/parsers';

import CSVParsingWorker from '@/pages/product/product/bulk/CSVParsingWorker?worker';
import * as Comlink from 'comlink';
import { ProductCSVParser } from '@/pages/product/product/bulk/bulkUploadHelper';
import { Info } from 'lucide-react';

const getWorker = () => {
  const worker = new (CSVParsingWorker as any)();
  return Comlink.wrap<ProductCSVParser>(worker);
};

const createTemplate = () => {
  const headings = [
    'Name',
    'Product ID',
    'Region',
    'Price',
    'Applicable Request Types',
    'Base Pack For',
    'Item Rate For',
    'Max Chargeable Weight (kg)',
    'Is Item Rate Override',
    'Is Exclusive Crate',
    'Add-On Category',
    'Covered By Warranty',
    'Description'
  ];
  const examples = [
    [
      'Standard Deployment Rate',
      'RC-101',
      'UK',
      '50.00',
      'DeployInventory | OffboardUser',
      'DeployInventory',
      'None',
      '25',
      'No',
      'No',
      'None',
      'No',
      ''
    ]
  ];

  const head = headings.join(',');
  return examples.reduce((template, row) => {
    return template + '\n' + row.join(',');
  }, head);
};

export const RateCardUploadStep = () => {
  const [onComplete, canMoveOn] = useCSV(state => [state.onComplete, !!state.result]);
  const { skipToStep } = useControls();

  const template = useMemo(() => {
    const templateText = createTemplate();
    return URL.createObjectURL(new Blob([templateText], { type: 'octet/stream' }));
  }, []);

  const processAndProceed = async (file: File) => {
    const instance = getWorker();
    const result = await instance.processRateCardCSV(file);

    onComplete(result);

    if (
      result.errors.couldNotParse.length ||
      result.errors.couldNotValidate.length ||
      result.errors.genericErrors?.length
    ) {
      skipToStep('issues');
      return;
    }

    skipToStep('review');
  };

  const validate = useCallback(() => canMoveOn, [canMoveOn]);
  return (
    <Step
      validate={validate}
      controls="manual"
      id="upload"
      title="Upload"
      description="Upload a spreadsheet of rate cards to bulk import them."
    >
      <div className="flex w-full flex-col gap-2">
        <div className="flex bg-blue-50 text-blue-800 p-4 rounded-md items-start gap-3 mb-4 text-sm">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex flex-col gap-2">
            <h4 className="font-semibold text-base">How this import works:</h4>
            <p>This tool safely updates your catalog row by row.</p>
            <ul className="list-disc list-inside flex flex-col gap-1">
              <li><strong>Updates:</strong> If a rate card in your file matches an existing one, its details and pricing will be updated.</li>
              <li><strong>Additions:</strong> If it is brand new, it will be added to the system.</li>
              <li><strong>Safe:</strong> Any existing rate cards in your system that are not included in your spreadsheet will be left completely untouched.</li>
            </ul>
          </div>
        </div>
        <a className={buttonClassName(ButtonVariant.Mono)} href={template} download="rate-cards-template.csv">
          <SiDownload />
          <span>Download template</span>
        </a>
        <FileInput
          id="csv"
          placeholder="Drop or click to upload spreadsheet"
          value={[]}
          onChange={([file]) => processAndProceed(file)}
        />
      </div>
    </Step>
  );
};
